import { useEffect, useState, useRef } from 'react';
import { useBlobFileStorage } from 'components/core/BlobFileStorage';
import { DocumentCreateModel } from 'shared/domain/document/documentCreateModel';
import {
  MAX_DISPLAY_FILE_SIZE,
  MAX_FILES,
  MAX_FILE_NAME_LENGTH,
} from 'shared/domain/document/documentLimits';
import {
  deduplicateFileList,
  filterOutDuplicatesOfNewFiles,
  processInitialFiles,
  preventOverflow,
} from './uploaderFileUtils';
import { DocumentBindings } from 'shared/domain/document/documentModel';
import { documentClient } from 'shared/domain/document/documentClient';
import { saveDocuments } from 'services/documentUpload';
import { showErrorToastWithMessage } from 'services/toaster';
import { debugLog } from 'shared/logger/debugLog';
import {
  areLocalIdsEqual,
  areObjectIdsEqual,
  isLocalId,
  isObjectIdString,
} from 'helpers/idComparison';
import { DEFAULT_CONFIG } from './constants';
import {
  areEqual,
  atomicCall,
} from './helpers';
import {
  Changes,
  ChangeSet,
  CONFIG_TYPE,
  DrawingType,
  FileReadSuccess,
  FileReadError,
  FileReadResult,
  FILE_STATE_KIND,
  GU,
  isUnsavedFileType,
  ResultType,
  shouldIncludeDownloadSrcInCleanup,
  UseGraphicUploader,
  UploaderFile,
} from './types';
import { toDrawingModel } from 'presentation/document/drawingToDrawingModel';
import { resolveExtensionFromFileName } from 'shared/utils/fileExtensions';
import { DOCUMENT_ERROR_SOURCE } from 'shared/domain/document/documentError';
import {
  DisplayedFile,
  DisplayedFilesStore,
  useStoreFiles,
} from '../gallery/filesStore';
import { DocumentOnView } from 'presentation/document/documentOnView';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { useMountedRef } from 'hooks/useMountRef';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { ImageRotation } from 'shared/dtos/out/document';
import { areArraysShallowEqual } from 'shared/utils/array';
import {
  collectBlobUrls,
  revokeRemovedBlobUrls,
  revokeBlobUrls,
} from 'shared/utils/blobUrl';
import { isDefinedValue } from 'shared/utils/value';
import {
  alignDrawingsWithFiles,
} from './drawingAlignment';
import { preserveResolvedPreviewState, hasRemoteDrawingRevisionChanged, invalidateStaleDrawingSignedUrls } from 'store/document/documentFileStateMutations';
import { clearDocumentPreviewCache } from './previewUrlsCache';
import { makeDownloadFile } from './downloadFile';
import { fileIdSelector } from './model';
import { FileTranslationKeys } from 'intl/keys';
import { enrichPdfUploadPageCountsBatch } from 'services/fileService/pdfConvertStatusService';

type ProcessSingleFile = (
  file: File,
  config: CONFIG_TYPE
) => Promise<FileReadResult>;

const truncateFileName = (fileName: string, maxLength: number): string => {
  if (fileName.length <= maxLength) return fileName;
  const extIndex = fileName.lastIndexOf('.');
  const ext = extIndex > 0 ? fileName.slice(extIndex) : '';
  const name = fileName.slice(
    0,
    extIndex > 0 ? extIndex : fileName.length
  );
  return name.slice(0, maxLength - ext.length) + ext;
};

function toFileReadResult(
  resolve: (result: FileReadResult) => void,
  file: File,
  config: CONFIG_TYPE
): void {
  if ((config.max_file_size || 0) < file.size) {
    return resolve({
      resultType: ResultType.error,
      error: 'max_file_size',
    } as FileReadError);
  }

  const fileSource = URL.createObjectURL(file);

  return resolve({
    file,
    resultType: ResultType.success,
    fileStateKind: FILE_STATE_KIND.UNSAVED,
    src:
      file.size > MAX_DISPLAY_FILE_SIZE
        ? DOCUMENT_ERROR_SOURCE
        : fileSource,
    size: file.size,
    title: truncateFileName(file.name, MAX_FILE_NAME_LENGTH),
    description: undefined,
    type: file.type,
    data: {
      extension: resolveExtensionFromFileName(file.name),
      name: file.name,
    },
    downloadSrc: fileSource,
    syncStatus: SyncStatus.PENDING,
    drawingSyncStatus: SyncStatus.PENDING,
    thumbnail:
      file.size > MAX_DISPLAY_FILE_SIZE
        ? DOCUMENT_ERROR_SOURCE
        : fileSource,
    imageRotation: ImageRotation.ROTATE_0,
  } as FileReadSuccess);
}

const processSingleFile: ProcessSingleFile = (file, config) =>
  new Promise((res) => {
    if (!file) {
      const error: FileReadError = {
        resultType: ResultType.error,
        error: 'no_file',
      };
      return res(error);
    }
    toFileReadResult(res, file, config);
  });

const failed = (res: FileReadResult[]): FileReadError[] => {
  return res.filter(
    (elem): elem is FileReadError => elem.resultType === ResultType.error
  );
};

const showErrors = (
  failedFiles: FileReadError[],
  filesLength: number
): void => {
  const overflow = filesLength > MAX_FILES ? filesLength - MAX_FILES : 0;

  if (overflow) {
    return showErrorToastWithMessage(FileTranslationKeys.tooManyFiles);
  }

  if (failedFiles.length) {
    if (failedFiles.length > 1) {
      return showErrorToastWithMessage(FileTranslationKeys.manyFilesError, {
          amount: failedFiles.length,
        });
    } else {
      return showErrorToastWithMessage(FileTranslationKeys.fileUploadErrorFileTooBig);
    }
  }
};

function toCreateDocumentModel(
  bindings: DocumentBindings,
  file: FileReadSuccess
): DocumentCreateModel {
  return {
    data: file.data as any,
    description: file.description,
    title: file.title,
    type: file.type,
    deleted: false,
    localData: file.downloadSrc,
    syncStatus: SyncStatus.PENDING,
    drawingSyncStatus: SyncStatus.PENDING,
    imageRotation: file.imageRotation,
    ...bindings,
  };
}

// @ts-ignore WIP LOCAL GU uses UseGraphicUploader types while in progress PT-3769
export const useGraphicUploader: UseGraphicUploader = (
  initialFiles,
  settings
) => {
  const config = { ...DEFAULT_CONFIG, ...settings };
  const documentScopeKey = config.documentScopeKey;
  const blobFileStorage = useBlobFileStorage();
  const [initialFilesState, setInitialFilesState] = useState(
    initialFiles || []
  );
  const [loading, setLoading] = useState(false);
  const filesStore = useStoreFiles<DocumentOnView | FileReadSuccess>(
    processInitialFiles(
      initialFilesState,
      config.mainImageHandlerRef?.current
    )
  );
  useFilesUploadRemoteUpdate(filesStore);
  const [drawings, setDrawings] = useState<DrawingType[]>([]);
  const drawingsRef = useRef<DrawingType[]>([]);
  const previousFilesRef = useRef(filesStore.get());
  const previousDocumentScopeKeyRef = useRef(documentScopeKey);
  const [changeSet, setChangeset] = useState<ChangeSet>({});

  useEffect(() => {
    drawingsRef.current = drawings;
  }, [drawings]);

  const handleChangeSet = (
    localId: number | undefined,
    changes: Changes
  ): void => {
    if (!localId) {
      return;
    }
    const newChangeset = { ...changeSet };
    const place = newChangeset[localId];
    newChangeset[localId] = {
      ...(place || {}),
      ...changes,
    };
    setChangeset(newChangeset);
  };

  useEffect(() => {
    if (!areEqual(initialFilesState, initialFiles || [])) {
      setInitialFilesState(initialFiles || []);
    }
  }, [initialFilesState, initialFiles]);

  useEffect(() => {
    const deduplicatedInitialFiles = deduplicateFileList(initialFilesState);
    const nextInitialFiles = processInitialFiles(
      deduplicatedInitialFiles,
      config.mainImageHandlerRef?.current
    );

    const previousDocumentScopeKey = previousDocumentScopeKeyRef.current;
    const sameScope =
      !previousDocumentScopeKey ||
      !documentScopeKey ||
      previousDocumentScopeKey === documentScopeKey;

    // Files added locally via `addFile` but not yet synced to the server
    // (fileStateKind UNSAVED, no `_id`/`localId`) have no counterpart in
    // `initialFiles` (server documents). `preserveResolvedPreviewState` only
    // merges by `_id`/`localId`, so without this they get silently dropped
    // whenever this effect re-runs for any unrelated reason (e.g. the parent
    // issue's document list re-emitting) — even while the gallery is open on
    // that exact file, leaving `filesStore` without an entry for it.
    //
    // Once the server confirms the same file, drop the local stand-in so it
    // isn't shown twice. `filterOutDuplicatesOfNewFiles`'s name+size identity
    // check can't catch this on its own: `DocumentOnView` (server documents,
    // in `nextInitialFiles`) has no `size` field at all, so it never equals
    // the local file's real byte size — match by `localId` first (assigned
    // client-side well before the server document appears in `initialFiles`)
    // and only fall back to the name+size check for the brief window before
    // that assignment lands.
    const nextInitialLocalIds = new Set(
      nextInitialFiles
        .map((file) => file.localId)
        .filter((localId): localId is number => typeof localId === 'number')
    );
    const pendingUnsavedFiles = sameScope
      ? filterOutDuplicatesOfNewFiles(
          filesStore
            .get()
            .filter(
              (file) =>
                isUnsavedFileType(file) &&
                !(
                  typeof file.localId === 'number' &&
                  nextInitialLocalIds.has(file.localId)
                )
            ),
          nextInitialFiles
        )
      : [];

    const mergedFiles = preserveResolvedPreviewState(
      filesStore.get(),
      [...pendingUnsavedFiles, ...nextInitialFiles],
      {
        previousScopeKey: previousDocumentScopeKey,
        nextScopeKey: documentScopeKey,
      }
    );
    filesStore.set(mergedFiles);

    previousDocumentScopeKeyRef.current = documentScopeKey;
  }, [
    documentScopeKey,
    filesStore,
    initialFilesState,
    config.mainImageHandlerRef,
  ]);

  useEffect(() => {
    return filesStore.subscribe(() => {
      const previousFiles = previousFilesRef.current;
      const nextFiles = filesStore.get();
      const currentDrawings = drawingsRef.current;

      revokeRemovedBlobUrls(previousFiles, nextFiles, {
        includeSrc: shouldIncludeDownloadSrcInCleanup,
        includeDownloadSrc: shouldIncludeDownloadSrcInCleanup,
        includeThumbnail: shouldIncludeDownloadSrcInCleanup,
      });

      const findPreviousFile = (
        nextFile: typeof nextFiles[number]
      ) =>
        previousFiles.find((file) => {
          if (
            isObjectIdString(nextFile._id) &&
            areObjectIdsEqual(file._id, nextFile._id)
          ) {
            return true;
          }

          if (
            isLocalId(nextFile.localId) &&
            areLocalIdsEqual(file.localId, nextFile.localId)
          ) {
            return true;
          }

          return false;
        });

      const alignedDrawings = alignDrawingsWithFiles(
        previousFiles,
        nextFiles,
        currentDrawings
      );

      alignedDrawings.forEach((drawing, index) => {
        const nextFile = nextFiles[index];
        const prevFile = findPreviousFile(nextFile);

        if (
          !prevFile ||
          !hasRemoteDrawingRevisionChanged(prevFile, nextFile)
        ) {
          return;
        }

        if (isLocalId(nextFile.localId)) {
          clearDocumentPreviewCache({
            _id: nextFile._id,
            localId: nextFile.localId,
          });
        }

        if (drawing?.mergedImageUrl) {
          blobFileStorage.removeItem(drawing.mergedImageUrl);
        }
      });

      const updatedDrawings = alignedDrawings.map((drawing, index) => {
        const nextFile = nextFiles[index];
        const prevFile = findPreviousFile(nextFile);

        if (
          !prevFile ||
          !hasRemoteDrawingRevisionChanged(prevFile, nextFile)
        ) {
          return drawing;
        }

        return undefined;
      });

      if (!areArraysShallowEqual(currentDrawings, updatedDrawings)) {
        setDrawings(updatedDrawings);
      }

      previousFilesRef.current = nextFiles;
    });
  }, [filesStore, blobFileStorage]);

  useEffect(() => {
    return () => {
      const blobUrls = new Set<string>();
      filesStore.get().forEach((file) => {
        collectBlobUrls(file, {
          includeSrc: shouldIncludeDownloadSrcInCleanup,
          includeDownloadSrc: shouldIncludeDownloadSrcInCleanup,
          includeThumbnail: shouldIncludeDownloadSrcInCleanup,
        }).forEach((url) => {
          blobUrls.add(url);
        });
      });

      drawingsRef.current.forEach((drawing) => {
        if (drawing?.mergedImageUrl) {
          blobFileStorage.removeItem(drawing.mergedImageUrl);
        }
      });

      revokeBlobUrls(blobUrls);
    };
  }, [blobFileStorage, filesStore]);

  const addFile: GU['addFile'] = async (filesToAdd) => {
    if (config.previewOnly) {
      return;
    }

    const processedFiles: FileReadResult[] = await Promise.all(
      filesToAdd.map((file) => processSingleFile(file, config))
    );
    const failedFiles = failed(processedFiles);
    const successFiles = processedFiles.filter(
      (elem): elem is FileReadSuccess => !(elem as FileReadError).error
    ).map((file) => ({
      ...file,
      loading: false,
    }));

    const successFilesWithConvertStatus =
      await enrichPdfUploadPageCountsBatch(successFiles);

    // Place newly uploaded files at the beginning so they appear immediately
    // at the front of the list instead of being appended at the end.
    // Filter out existing items that match new files (name+size) to avoid duplicates
    // e.g. when change event fires twice or initial state is reapplied.
    const existingWithoutDuplicates = filterOutDuplicatesOfNewFiles(
      filesStore.get(),
      successFilesWithConvertStatus
    );
    const combined: UploaderFile<
      DocumentOnView | FileReadSuccess
    >[] = [...successFilesWithConvertStatus, ...existingWithoutDuplicates];
    const allSuccessfulFiles = deduplicateFileList(combined);

    const newSet: UploaderFile<DocumentOnView | FileReadSuccess>[] =
      allSuccessfulFiles.slice(0, MAX_FILES);
    const toAdd = preventOverflow(
      existingWithoutDuplicates.length,
      successFilesWithConvertStatus
    );
    showErrors(failedFiles, allSuccessfulFiles.length);

    filesStore.set(newSet);

    const fallback = (): void => {
      if (!config.baseUrl) {
        return;
      }
      // TODO: GU remove ! PT-3769
      const bindings = config
        .documentBinderFactory?.(config.baseUrl)
        .binding()!;
      saveDocuments(
        config.baseUrl,
        toAdd.map((fileToAdd) =>
          toCreateDocumentModel(bindings, fileToAdd)
        )
      )
        .then((res) => {
          let index = 0;
          const prev = filesStore.get();
          const newFiles = prev.map((file) => {
            if (file.localId === undefined) {
              file.localId = res[index];
              file.fileStateKind = FILE_STATE_KIND.SAVED;
              index += 1;
            }
            return file;
          });
          filesStore.set(newFiles);
          debugLog('save document locally response', res);
        })
        .catch((error) => {
          debugLog('error save documents locally', error);
        });
    };

    atomicCall(
      config,
      'upload',
      [toAdd, drawings, filesStore.get()],
      fallback
    );
  };

  const editFile: GU['editFile'] = (
    index,
    descriptionKey,
    newDescriptionValue
  ) => {
    if (config.previewOnly) {
      return;
    }
    const newSet = [...filesStore.get()];
    newSet[index] = {
      ...newSet[index],
      [descriptionKey]: newDescriptionValue,
    };
    filesStore.set(newSet);
    if (!config.atomicChanges) {
      handleChangeSet(newSet[index].localId, {
        [descriptionKey]: newDescriptionValue,
      });
    }
    const fallback = (): Promise<unknown> => {
      return documentClient.edit({
        localId: newSet[index].localId!,
        description: newDescriptionValue as string,
      }).catch((error: unknown) => {
        debugLog('error', error);
      });
    };

    atomicCall(
      config,
      'edit',
      [filesStore.get()[index], { [descriptionKey]: newDescriptionValue }],
      fallback
    );
  };

  const rotateFile: GU['rotateFile'] = (index, newRotationValue) => {
    if (config.previewOnly) {
      return;
    }

    const newSet = [...filesStore.get()];
    newSet[index] = {
      ...newSet[index],
      imageRotation: newRotationValue,
    };

    filesStore.set(newSet);
    if (!config.atomicChanges) {
      handleChangeSet(newSet[index].localId, {
        imageRotation: newRotationValue,
      });
    }
    const fallback = (): Promise<unknown> => {
      return documentClient.edit({
        localId: newSet[index].localId!,
        imageRotation: newRotationValue,
      }).catch((error: unknown) => {
        debugLog('error', error);
      });
    };

    atomicCall(
      config,
      'rotate',
      [filesStore.get()[index], { imageRotation: newRotationValue }],
      fallback
    );
  };

  const removeFile: GU['removeFile'] = (id) => {
    if (config.previewOnly) {
      return;
    }
    const file = filesStore.get()[id];
    debugLog('removeFile clicked', {
      id,
      localId: file?.localId,
      atomicChanges: Boolean(config.atomicChanges),
      hasBaseUrl: Boolean(config.baseUrl),
      hasCustomRemoveAction: Boolean(config.actions?.remove),
      fileStateKind: file?.fileStateKind,
    });
    const fallback = (): void => {
      if (typeof file.localId === 'number') {
        debugLog('atomicCall callRemoveFile', id, file.localId);

        documentClient.remove(file.localId)
          .then(() => {
            debugLog('removeFile atomic call result', file.localId);
          })
          .catch((error) => {
            debugLog('removeFile atomic call error', error);
          });
      } else {
        debugLog('removeFile fallback skipped: missing localId', {
          id,
          localId: file?.localId,
        });
      }
    };
    atomicCall(config, 'remove', [file], fallback);

    if (!config.atomicChanges) {
      debugLog(
        'removeFile queued in changeSet (atomicChanges=false); will be sent on save/executeChangeset',
        {
          id,
          localId: file?.localId,
        }
      );
      handleChangeSet(file.localId, { deleted: true });
    }

    revokeBlobUrls(collectBlobUrls(file));

    const drawingToRemove = drawingsRef.current[id];
    if (drawingToRemove?.mergedImageUrl) {
      blobFileStorage.removeItem(drawingToRemove.mergedImageUrl);
    }

    const newSet = [...filesStore.get()];
    newSet.splice(id, 1);
    filesStore.set(newSet);

    setDrawings((prev) => {
      const newDrawings = [...prev];
      if (newDrawings[id]) {
        newDrawings.splice(id, 1);
      }
      return newDrawings;
    });
  };

  const addDrawing: GU['addDrawing'] = (drawing, index) => {
    if (config.previewOnly) {
      return;
    }
    const previousDrawing = drawingsRef.current[index];

    if (previousDrawing?.mergedImageUrl) {
      blobFileStorage.removeItem(previousDrawing.mergedImageUrl);
    }

    if (drawing?.mergedImageUrl) {
      blobFileStorage.setItem(
        drawing.mergedImageUrl,
        drawing.mergedImageUrl
      );
    }
    setDrawings((prev: DrawingType[]): DrawingType[] => {
      const newSet: DrawingType[] = [...prev];
      newSet[index] = drawing;
      return newSet;
    });

    const fallback = (): void => {
      const currentDrawings: DrawingType[] = [];
      currentDrawings[index] = drawing;
      debugLog('save drawing locally');
      const localId = filesStore.get()[index].localId;
      if (localId === undefined) {
        return;
      }
      documentClient.createDrawing(toDrawingModel(drawing), localId).catch((error: unknown) => {
        debugLog('error save drawing locally', error);
      });
    };

    atomicCall(
      config,
      'uploadDrawing',
      [filesStore.get(), drawing, index],
      fallback
    );
  };

  const saveFilesLocally: GU['uploadFiles'] = (
    baseUrl,
    onDone,
    options
  ) => {
    if (config.previewOnly || !baseUrl) {
      return;
    }
    // TODO: GU remove ! PT-3769
    const bindings = config.documentBinderFactory?.(baseUrl).binding()!;
    const filesToSave = filesStore
      .get()
      .filter(
        (file): file is DisplayedFile<FileReadSuccess> => !file.localId
      )
      .map((fileToSave) => toCreateDocumentModel(bindings, fileToSave));

    debugLog('saveFilesLocally, uploadFiles', filesToSave);
    return saveDocuments(baseUrl, filesToSave)
      .then((res) => {
        let recentlySavedFileIdsIndex = 0;
        const allSavedFiles = filesStore.get().map((file) => {
          if (file.localId === undefined) {
            const localId = res[recentlySavedFileIdsIndex];
            recentlySavedFileIdsIndex += 1;
            if (localId === undefined) {
              throw new Error(
                'Cannot assign undefined to localId property of saved file.'
              );
            }
            file.localId = localId;
            return {
              ...file,
              localId,
            } as FileReadSuccess & {
              localId: number;
            };
          }
          return file as FileReadSuccess & {
            localId: number;
          };
        });

        const drawingPromises: Promise<void>[] = [];
        drawings.forEach((drawing, index) => {
          if (!drawing) {
            return;
          }

          drawingPromises.push(
            documentClient.createDrawing(toDrawingModel(drawing), allSavedFiles[index].localId)
          );
        });

        return Promise.all(drawingPromises);
      })
      .then(() => {
        setLoading(false);
        if (onDone) return onDone();
      });
  };

  const executeChangeset = (): Promise<unknown>[] => {
    const asArray = Object.entries(changeSet).map(([key, value]) => ({
      localId: parseFloat(key),
      changes: value,
    }));

    const toRemove = asArray.filter((elem) => elem.changes.deleted);
    const toEdit = asArray.filter((elem) => !elem.changes.deleted);

    const toRemoveCalls = toRemove.map((data) => {
      debugLog('toRemoveCalls', data);
      return documentClient.remove(data.localId);
    });
    const toEditCalls = toEdit.map((data) => {
      debugLog('toEditCalls', data);
      const changes: {
        description?: string;
        imageRotation?: ImageRotation;
      } = {};
      if (data.changes.description) {
        changes.description = data.changes.description as string;
      }
      if (isDefinedValue(data.changes.imageRotation)) {
        changes.imageRotation = data.changes
          .imageRotation as ImageRotation;
      }
      return documentClient.edit({
        localId: data.localId,
        ...changes,
      });
    });

    return [...toRemoveCalls, ...toEditCalls];
  };
  return {
    filesStore,
    drawings,
    addDrawing,
    addFile,
    editFile,
    removeFile,
    rotateFile,
    uploadFiles: saveFilesLocally,
    loading,
    config,
    executeChangeset,
    changeSet,
  };
};

/**
 * `updatedEntity` carries only the local id, never the stored document, so the
 * drawing revision after the change is unknown: a file with any drawing state
 * is treated as possibly redrawn.
 */
const UNKNOWN_DRAWING_REVISION = {};

function useFilesUploadRemoteUpdate(
  filesStore: DisplayedFilesStore<DocumentOnView | FileReadSuccess>
): void {
  const mountedRef = useMountedRef();
  // Fetching the real signed URL as soon as the upload is confirmed (rather
  // than waiting for a UI component to notice `_id` on its own, e.g. via an
  // IntersectionObserver-gated prefetch) makes the gallery update reliably
  // regardless of whether the affected thumbnail happens to be on screen.
  const downloadFileRef = useRef(
    makeDownloadFile(
      fileIdSelector,
      undefined,
      filesStore as DisplayedFilesStore
    )
  );
  useEffect(() => {
    downloadFileRef.current = makeDownloadFile(
      fileIdSelector,
      undefined,
      filesStore as DisplayedFilesStore
    );
  }, [filesStore]);

  useEffect(() => {
    const refreshDrawnPreview = (
      localId: number,
      { onlyWhenRedrawn }: { onlyWhenRedrawn: boolean }
    ): void => {
      const currentFile = filesStore.get().find((d) => areLocalIdsEqual(d.localId, localId));
      if (!currentFile) {
        return;
      }

      const possiblyRedrawn = hasRemoteDrawingRevisionChanged(
        {
          drawingSyncStatus: currentFile.drawingSyncStatus,
          data: currentFile.data ?? undefined,
        },
        UNKNOWN_DRAWING_REVISION
      );
      if (onlyWhenRedrawn && !possiblyRedrawn) {
        return;
      }

      clearDocumentPreviewCache({
        localId,
        _id: currentFile._id,
      });

      invalidateStaleDrawingSignedUrls(
        filesStore as DisplayedFilesStore,
        localId
      );

      const updatedFile = filesStore
        .get()
        .find((entry) => areLocalIdsEqual(entry.localId, localId));
      if (updatedFile) {
        downloadFileRef.current(updatedFile);
      }
    };

    return documentClient.onEvent((event): void => {
      if (!mountedRef.current || !event.data) {
        return;
      }

      switch (event.type) {
        case DomainMessagesTypes.uploadedEntity: {
          const { localId, _id } = event.data;
          const files = filesStore.get();
          const index = files.findIndex((d) => areLocalIdsEqual(d.localId, localId));
          if (index === -1) return;

          const updated = files.map((f, i) =>
            i === index
              ? {
                  ...f,
                  _id,
                  fileStateKind: FILE_STATE_KIND.SAVED,
                  loading: false,
                }
              : f
          );
          filesStore.set(updated);
          downloadFileRef.current(updated[index]);
          return;
        }
        case DomainMessagesTypes.updatedEntity:
          refreshDrawnPreview(event.data, { onlyWhenRedrawn: true });
          return;
        case DomainMessagesTypes.createdDrawing:
          refreshDrawnPreview(event.data.localId, { onlyWhenRedrawn: false });
          return;
      }
    });
  }, [mountedRef, filesStore]);
}
